import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Car,
  DollarSign,
  Eye,
  Flame,
  Gauge,
  Layers,
  MessageSquare,
  Percent,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { KpiCard } from "@/components/admin/KpiCard";
import { ResourceState } from "@/components/admin/ResourceState";
import { useDashboardAnalytics } from "@/application/hooks/use-dashboard-analytics";
import { useLeads } from "@/application/hooks/use-leads";
import { useSeoAnalytics } from "@/application/hooks/use-seo-analytics";
import { useVehicles } from "@/application/hooks/use-vehicles";
import { formatPrice } from "@/domain/models/vehicle";
import { useI18n } from "@/i18n";

const CHART_COLORS = ["#4EA8FF", "#7EC7FF", "#2C7DE0", "#A9D5FF", "#5C6B85", "#8CA6C7"];

function ChartCard({
  title,
  subtitle,
  action,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-border/60 bg-surface-elevated/60 p-5 shadow-sm backdrop-blur ${className}`}
    >
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-base font-semibold">{title}</h3>
          {subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function DashboardPage() {
  const { t, tf } = useI18n();
  const vehiclesQuery = useVehicles();
  const leadsQuery = useLeads();
  const dashboardQuery = useDashboardAnalytics();
  const seoQuery = useSeoAnalytics();

  const vehicles = vehiclesQuery.data ?? [];
  const leads = leadsQuery.data ?? [];
  const analytics = dashboardQuery.data;
  const coreWebVitals = seoQuery.data?.coreWebVitals;

  const loading =
    vehiclesQuery.status === "loading" ||
    leadsQuery.status === "loading" ||
    dashboardQuery.status === "loading" ||
    seoQuery.status === "loading" ||
    vehiclesQuery.status === "idle" ||
    leadsQuery.status === "idle" ||
    dashboardQuery.status === "idle" ||
    seoQuery.status === "idle";

  const firstError =
    vehiclesQuery.error || leadsQuery.error || dashboardQuery.error || seoQuery.error;

  const combinedStatus = loading
    ? "loading"
    : firstError
      ? "error"
      : !analytics || !coreWebVitals
        ? "empty"
        : "success";

  const totalInventory = vehicles.length;
  const available = vehicles.filter((v) => v.available).length;
  const inventoryValue = vehicles.reduce((s, v) => s + v.price, 0);
  const avgPrice = totalInventory ? inventoryValue / totalInventory : 0;
  const unread = leads.filter((l) => l.status === "New").length;
  const conversion = leads.length
    ? (leads.filter((l) => l.status === "Won").length / leads.length) * 100
    : 0;

  const {
    dailyTraffic = [],
    trafficSources = [],
    revenueByMonth = [],
    brandDistribution = [],
    funnelStages = [],
    topVehicles = [],
  } = analytics ?? {};

  return (
    <div>
      <PageHeader
        title={t.admin.dashboard.title}
        description={t.admin.dashboard.description}
        actions={
          <>
            <div className="hidden items-center gap-1 rounded-full border border-border/60 bg-surface/60 p-1 sm:flex">
              {["24h", "7d", "30d", "12m"].map((k, i) => (
                <button
                  key={k}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    i === 2
                      ? "bg-primary text-primary-foreground shadow-[var(--shadow-glow)]"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
            <Link
              to="/admin/vehicles/new"
              className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition hover:-translate-y-0.5"
            >
              <Car className="h-3.5 w-3.5" /> {t.admin.dashboard.newListing}
            </Link>
          </>
        }
      />

      <ResourceState
        status={combinedStatus}
        error={firstError}
        onRetry={() => {
          void vehiclesQuery.reload();
          void leadsQuery.reload();
          void dashboardQuery.reload();
          void seoQuery.reload();
        }}
      >
      {analytics && coreWebVitals ? (
      <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label={t.admin.dashboard.totalInventory} value={totalInventory} icon={Car} delta={4.2} hint={t.admin.common.vsLastMonth} />
        <KpiCard label={t.admin.dashboard.availableNow} value={available} icon={Layers} delta={2.1} hint={tf(t.admin.dashboard.reservedHint, { count: totalInventory - available })} />
        <KpiCard label={t.admin.dashboard.inventoryValue} value={inventoryValue} icon={Wallet} format="currency" delta={7.8} />
        <KpiCard label={t.admin.dashboard.avgPrice} value={avgPrice} icon={DollarSign} format="currency" delta={1.4} />
        <KpiCard label={t.admin.dashboard.totalLeads} value={leads.length} icon={Users} delta={12.6} />
        <KpiCard label={t.admin.dashboard.unreadInquiries} value={unread} icon={MessageSquare} delta={-3.1} hint={t.admin.dashboard.needsTriage} />
        <KpiCard label={t.admin.dashboard.conversionRate} value={conversion} format="percent" icon={Percent} delta={0.8} />
        <KpiCard label={t.admin.dashboard.featuredVehicles} value={vehicles.filter((v) => v.tag).length} icon={Flame} delta={0} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          className="lg:col-span-2"
          title={t.admin.dashboard.trafficTitle}
          subtitle={t.admin.dashboard.trafficSubtitle}
        >
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dailyTraffic} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="v" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4EA8FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#4EA8FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="l" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7EC7FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#7EC7FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.3)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 12,
                  fontSize: 12,
                }}
              />
              <Area type="monotone" dataKey="visits" stroke="#4EA8FF" strokeWidth={2} fill="url(#v)" />
              <Area type="monotone" dataKey="leads" stroke="#7EC7FF" strokeWidth={2} fill="url(#l)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t.admin.dashboard.sourcesTitle} subtitle={t.admin.dashboard.sourcesSubtitle}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={trafficSources}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={95}
                paddingAngle={3}
                stroke="none"
              >
                {trafficSources.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 12,
                  fontSize: 12,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            {trafficSources.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                />
                <span className="text-muted-foreground">{s.name}</span>
                <span className="ml-auto font-semibold tabular-nums">{s.value}%</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          className="lg:col-span-2"
          title={t.admin.dashboard.revenueTitle}
          subtitle={t.admin.dashboard.revenueSubtitle}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenueByMonth} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.3)" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                formatter={(v: number) => formatPrice(v)}
              />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#4EA8FF" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t.admin.dashboard.brandMix} subtitle={t.admin.dashboard.brandMixSubtitle}>
          <div className="space-y-3">
            {brandDistribution.map((b, i) => {
              const max = Math.max(...brandDistribution.map((x) => x.value));
              return (
                <div key={b.name}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium">{b.name}</span>
                    <span className="text-muted-foreground tabular-nums">{b.value}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${(b.value / max) * 100}%`,
                        background: `linear-gradient(90deg, ${CHART_COLORS[i % CHART_COLORS.length]}, ${CHART_COLORS[(i + 1) % CHART_COLORS.length]})`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard title={t.admin.dashboard.funnelTitle} subtitle={t.admin.dashboard.funnelSubtitle} className="lg:col-span-1">
          <div className="space-y-2">
            {funnelStages.map((s, i) => {
              const pct = (s.value / funnelStages[0].value) * 100;
              return (
                <div key={s.stage}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium">{s.stage}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {s.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-8 overflow-hidden rounded-lg bg-muted">
                    <div
                      className="flex h-full items-center justify-end rounded-lg px-2 text-[10px] font-semibold text-primary-foreground transition-all duration-700"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${CHART_COLORS[i]}, ${CHART_COLORS[i + 1] ?? CHART_COLORS[0]})`,
                      }}
                    >
                      {pct.toFixed(1)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title={t.admin.dashboard.engagedTitle} subtitle={t.admin.dashboard.engagedSubtitle} className="lg:col-span-2">
          <div className="divide-y divide-border/60">
            {topVehicles.map((v) => (
              <div key={v.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <img src={v.image} alt="" className="h-12 w-16 shrink-0 rounded-md object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{v.label}</div>
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {v.views.toLocaleString()}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" /> {tf(t.admin.dashboard.leadsCount, { count: v.leads })}
                    </span>
                    <span className="text-primary">{v.price}</span>
                  </div>
                </div>
                <TrendingUp className="h-4 w-4 shrink-0 text-emerald-400" />
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: "LCP", value: `${coreWebVitals.lcp}s`, hint: "Largest Contentful Paint", good: coreWebVitals.lcp < 2.5 },
          { label: "INP", value: `${coreWebVitals.inp}ms`, hint: "Interaction to Next Paint", good: coreWebVitals.inp < 200 },
          { label: "CLS", value: coreWebVitals.cls.toFixed(2), hint: "Cumulative Layout Shift", good: coreWebVitals.cls < 0.1 },
          { label: "TTFB", value: `${coreWebVitals.ttfb}ms`, hint: "Time to First Byte", good: coreWebVitals.ttfb < 800 },
        ].map((m) => (
          <div
            key={m.label}
            className="rounded-2xl border border-border/60 bg-surface-elevated/60 p-4 backdrop-blur"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {m.label}
              </span>
              <Gauge className={m.good ? "h-4 w-4 text-emerald-400" : "h-4 w-4 text-amber-400"} />
            </div>
            <div className="mt-2 font-display text-2xl font-semibold tabular-nums">{m.value}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">{m.hint}</div>
          </div>
        ))}
      </div>
      </>
      ) : null}
      </ResourceState>
    </div>
  );
}
